import { Button, FAIcon, ModuleWrapper } from '@/components'
import type {
  VehicleDocumentsQuery,
  VehicleDocumentsQueryVariables,
} from '@/modules/vehicles'
import { VEHICLE_DOCUMENTS_QUERY } from '@/modules/vehicles'
import type { VehicleListFilterForm } from '@/modules/vehicles/list-filters'
import {
  emptyVehicleListFilter,
  getVehicleExpirationFilterVariables,
  VehicleListFilters,
} from '@/modules/vehicles/list-filters'
import { VehicleListExportButton } from '@/modules/vehicles/list-export'
import { PERMISSIONS } from '@/utils'
import {
  useDebounce,
  useNodes,
  useOnErrorAuto,
  usePermissions,
} from '@/utils/hooks'
import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useMemo, useState } from 'react'
import { useVehicleDocumentHandlers } from './use-vehicle-document-handlers'
import { VehicleDocumentsTable } from './vehicle-documents-table'

export const VehicleDocumentsView: React.FC = () => {
  const translations = useTranslate({
    title: 'vehicles.vehicle-documents',
    searchPlaceholder: 'vehicles.documents.search-placeholder',
    add: 'vehicles.new-vehicle-document',
  })

  const [filter, setFilter] = useState<VehicleListFilterForm>(
    emptyVehicleListFilter
  )

  // Only the free-text search is debounced; the other filters change one click
  // at a time and should apply immediately.
  const debouncedQ = useDebounce(filter.q)

  const filterVariables = useMemo(
    () => getVehicleExpirationFilterVariables({ ...filter, q: debouncedQ }),
    [filter, debouncedQ]
  )

  const onErrorAuto = useOnErrorAuto()

  const { data, loading } = useQuery<
    VehicleDocumentsQuery,
    VehicleDocumentsQueryVariables
  >(VEHICLE_DOCUMENTS_QUERY, {
    variables: { ...filterVariables, orderBy: 'expirationDate' },
    onError: onErrorAuto(),
  })

  const documents = useNodes(data?.allVehicleDocuments)

  const { addDocument, editDocument, removeDocument } =
    useVehicleDocumentHandlers()

  const { hasPermissions } = usePermissions()
  const canAdd = hasPermissions(PERMISSIONS.vehicles.add.vehicledocument, true)

  return (
    <ModuleWrapper
      title={translations.title}
      headerRight={
        <div className="flex items-center gap-2">
          <VehicleListExportButton kind="documents" filters={filterVariables} />
          {canAdd && (
            <Button onClick={() => void addDocument()}>
              <FAIcon icon="plus" />
              {translations.add}
            </Button>
          )}
        </div>
      }
    >
      <VehicleListFilters
        filter={filter}
        searchPlaceholder={translations.searchPlaceholder}
        onChange={patch => setFilter(current => ({ ...current, ...patch }))}
        onClear={() => setFilter(emptyVehicleListFilter)}
      />

      <VehicleDocumentsTable
        documents={documents}
        loading={loading}
        onEdit={editDocument}
        onDelete={removeDocument}
      />
    </ModuleWrapper>
  )
}
